<template>
  <div class="fuel-summary-cards mc-dashboard-card pt-3 pb-3">
    <b-row>
      <b-col v-for="card in getCards" md="3">
        <div class="fuel-summary-card">
          <div class="fuel-summary-card__content">
            <span class="fuel-summary-card__title">{{ $t(card.title) }}</span>
            <div class="fuel-summary-card__value">
              <div class="d-flex" v-if="card.value != null && !isLoading">
                <div>
                  <span>{{ card.value }}</span>
                  <span class="fuel-summary-card__unit">{{ card.format }}</span>
                </div>
                <div v-if="card.key == 'efficiencyAverage'">
                  <span class="ps-1 pe-1">&bull;</span>
                  <span>Rp{{ Number(fuelTypePriceAvg).toLocaleString('id-ID') }}</span>
                  <span class="fuel-summary-card__unit">/Km</span>
                </div>
              </div>
              <span v-else-if="card.value == null && !isLoading">{{
                $t('dashboard.text.noData')
              }}</span>
              <b-skeleton v-else width="80px"></b-skeleton>
            </div>
            <div
              v-if="!isLoading"
              class="fuel-summary-card__progress"
              :class="{
                'fuel-summary-card__progress--down': card.trend == 'D',
                'fuel-summary-card__progress--up': card.trend == 'U',
              }"
            >
              <div v-if="card.trendPercentage != null">
                <i v-if="card.trend == 'D'" class="mc-fa icon-fill_trending-down"></i>
                <i v-else class="mc-fa icon-fill_trending-up"></i>
                <span>{{ card.trendPercentage }}%</span>
              </div>
              <span v-else>- %</span>
            </div>
            <b-skeleton v-else width="50px"></b-skeleton>
          </div>
          <div class="fuel-summary-card__icon">
            <i :class="`mc-fa ${card.icon}`"></i>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import { SUMMARY_FUEL_DASHBOARD } from '@/services/api/dashboard/dashboard'
import moment from 'moment'
import { QueryCache } from '@/mixin/query-cache'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

moment.locale('id')

export default {
  props: {
    isLayoutEdit: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [Helper],
  data() {
    return {
      isLoading: false,
      cards: [
        {
          key: 'fuelUsage',
          title: 'dashboard.summary.fuelUsageTotal',
          icon: 'icon-outline_fuel',
          value: '0',
          trend: 'N',
          trendPercentage: 0,
          format: 'L',
        },
        {
          key: 'distance',
          title: 'dashboard.summary.distanceTotal',
          icon: 'icon-outline_pin-history',
          value: '0',
          trend: 'N',
          trendPercentage: 0,
          format: 'Km',
        },
        {
          key: 'efficiencyAverage',
          title: 'dashboard.summary.efficencyAverage',
          icon: 'icon-fill_fuel-decrease',
          value: '0',
          trend: 'N',
          trendPercentage: 0,
          format: 'Km/L',
        },
        {
          key: 'idle',
          title: 'dashboard.summary.idleTimeTotal',
          icon: 'icon-outline_truck-idle',
          value: '0 hrs',
          trend: 'N',
          trendPercentage: 0,
          format: '',
        },
      ],
      vgroupId: [],
      vehicleTypeId: [],
      period: [],
      groupBy: 'vehicle',
      queryCache: new QueryCache(),
      fuelTypePriceAvg: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    getCards() {
      return this.cards
    },
  },
  methods: {
    loadFilter(filter) {
      this.isLoading = true
      this.vgroupId = filter.vgroupId
      this.vehicleTypeId = filter.vehicleTypeId
      this.period = [filter.startDate, filter.endDate]
      const startDate = moment(filter.startDate)
      const endDate = moment(filter.endDate)
      const dateDiff = endDate.diff(startDate, 'day')
      const prevEndDate = startDate.clone().subtract(1, 'day')
      const prevStartDate = prevEndDate.clone().subtract(dateDiff, 'day')
      this.loadData({
        vgroupId: this.vgroupId,
        vehicleTypeId: this.vehicleTypeId,
        startDate: moment(this.period[0] + ' 00:00:00.000').toISOString(),
        endDate: moment(this.period[1] + ' 23:59:59.000').toISOString(),
        prevStartDate: moment(prevStartDate.format('YYYY-MM-DD') + ' 00:00:00.000').toISOString(),
        prevEndDate: moment(prevEndDate.format('YYYY-MM-DD') + ' 23:59:59.000').toISOString(),
        groupBy: this.groupBy,
      })
    },
    async loadData(data) {
      let filters = { ...data }
      let currentFilter = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        groupBy: filters.groupBy,
        disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
      }
      let previousFilter = {
        startDate: filters.prevStartDate,
        endDate: filters.prevEndDate,
        groupBy: filters.groupBy,
        disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
      }
      if (filters.vgroupId && filters.vgroupId.length > 0) {
        currentFilter = {
          ...currentFilter,
          vgroupId: filters.vgroupId,
        }
        previousFilter = {
          ...previousFilter,
          vgroupId: filters.vgroupId,
        }
      }
      if (filters.vehicleTypeId && filters.vehicleTypeId.length > 0) {
        currentFilter = {
          ...currentFilter,
          vehicleTypeId: filters.vehicleTypeId,
        }
        previousFilter = {
          ...previousFilter,
          vehicleTypeId: filters.vehicleTypeId,
        }
      }
      const currResult = await this.queryCache.fetch(
        SUMMARY_FUEL_DASHBOARD,
        {
          filter: currentFilter,
        },
        ''
      )
      const prevResult = await this.queryCache.fetch(
        SUMMARY_FUEL_DASHBOARD,
        {
          filter: previousFilter,
        },
        ''
      )
      const currFuel = currResult.summaryFuelDashboard.summaryFuelDashboard.total
      const prevFuel = prevResult.summaryFuelDashboard.summaryFuelDashboard.total
      const currTotal = this.sumAllValues(currFuel)
      const prevTotal = this.sumAllValues(prevFuel)

      this.cards[0].value = currTotal.fuelUsage.toFixed(2)
      this.cards[1].value = currTotal.distance.toFixed(2)
      this.cards[2].value = currTotal.efficiencyAverage
      this.cards[3].value = this.convertTimeToString(currTotal.idle)
      this.fuelTypePriceAvg = (
        currTotal.fuelTypePrice / Number(currTotal.distance.toFixed(2))
      ).toFixed(0)
      const fuelUsageTrend = this.trendPercentage(currTotal.fuelUsage, prevTotal.fuelUsage)
      const distanceTrend = this.trendPercentage(currTotal.distance, prevTotal.distance)
      const efficiencyAverageTrend = this.trendPercentage(
        currTotal.efficiencyAverage,
        prevTotal.efficiencyAverage
      )
      const idleTrend = this.trendPercentage(currTotal.idle, prevTotal.idle)
      this.cards[0].trend = fuelUsageTrend.trend
      this.cards[0].trendPercentage = fuelUsageTrend.trendPercentage
      this.cards[1].trend = distanceTrend.trend
      this.cards[1].trendPercentage = distanceTrend.trendPercentage
      this.cards[2].trend = efficiencyAverageTrend.trend
      this.cards[2].trendPercentage = efficiencyAverageTrend.trendPercentage
      this.cards[3].trend = idleTrend.trend
      this.cards[3].trendPercentage = idleTrend.trendPercentage
      this.isLoading = false
    },
    trendPercentage(curr, prev) {
      if (!prev) {
        return {
          trendPercentage: null,
          trend: 'N',
        }
      }
      const diff = (curr ?? 0) - prev
      const percentage = (diff / prev) * 100
      return {
        trendPercentage: Math.abs(Number(percentage.toFixed(1))),
        trend: percentage < 0 ? 'D' : 'U',
      }
    },
    sumAllValues(data) {
      const result = {
        fuelUsage: data.fuelConsumption,
        distance: data.distanceInKm,
        efficiencyAverage: data.fuelConsumption
          ? (data.distanceInKm / data.fuelConsumption).toFixed(2)
          : null,
        idle: data.idleDurationInSecond,
        fuelTypePrice: data.fuelTypePrice,
      }
      return result
    },
  },
}
</script>

<style scoped>
.fuel-summary-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 74px;
  padding: 14px 12px;
  background: #ffffff;
  border: 1px solid #e7ebf3;
  border-radius: 6px;
}

.fuel-summary-card__content {
  min-width: 0;
}

.fuel-summary-card__title {
  display: block;
  margin-bottom: 4px;
  color: #8a94a6;
  font-size: 11px;
  line-height: 16px;
}

.fuel-summary-card__value {
  display: flex;
  align-items: baseline;
  color: #253858;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
}

.fuel-summary-card__unit {
  margin-left: 4px;
  color: #667085;
  font-size: 11px;
  font-weight: 500;
}

.fuel-summary-card__progress {
  display: flex;
  align-items: center;
  margin-top: 5px;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
}

.fuel-summary-card__progress i {
  margin-right: 3px;
  font-size: 11px;
}

.fuel-summary-card__progress--up {
  color: #039855 !important;
}

.fuel-summary-card__progress--down {
  color: #f04438 !important;
}

.mc-fa::before {
  color: inherit !important;
}

.fuel-summary-card__icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-left: 12px;
  color: #ffffff;
  background: #6f93c5;
  border-radius: 50%;
}

.fuel-summary-card__icon i {
  font-size: 18px;
}
</style>
